import { isPrimaryColor } from '../../utils';
import { mode, StyleFunctionProps } from '@chakra-ui/theme-tools';
import type { ComponentStyleConfig } from '@chakra-ui/theme';

const Select: ComponentStyleConfig = {
  parts: [
    'SelectContainer',
    'SelectedOptionsContainer',
    'SelectedOptionTag',
    'SelectedOptionText',
    'RemoveIcon',
    'SearchBox',
    'ToggleButton',
    'ClearButton',
    'Icon',
    'OptionsModal',
    'NoOptions',
    'NoOptionsIcon',
    'Option',
    'Checkbox',
    'OptionContainer',
    'OptionIcon',
    'OptionLabel',
    'Tooltip',
  ],
  baseStyle: (props: StyleFunctionProps) => ({
    SelectContainer: {
      position: 'relative',
      borderColor: mode('gray.300', 'black')(props),
      rounded: 'md',
      borderWidth: '1px',
      boxShadow: 'sm',
      bg: mode('white', 'black')(props),
      ...(props.isInvalid ? { borderColor: 'red.500' } : {}),
    },
    SelectedOptionsContainer: {
      w: 'full',
      alignItems: 'center',
      flexWrap: 'wrap',
      bg: mode('white', 'black')(props),
      rounded: 'md',
    },
    SelectedOptionTag: {
      alignItems: 'center',
      h: 'fit-content',
      p: 1,
      mx: 1,
      bg: mode('gray.100', 'gray.800')(props),
      boxShadow: 'md',
      rounded: 'md',
      my: 1,
    },
    SelectedOptionText: {
      whiteSpace: 'nowrap',
      isTruncated: true,
    },
    RemoveIcon: {
      ml: 2,
      cursor: 'pointer',
    },
    SearchBox: {
      h: 8,
      w: props.isMulti ? 'unset' : 'full',
      _focus: {
        ring: '2px',
        outline: '0px',
        ringColor: 'blue.300',
        boxShadow: 'none',
      },
      bg: mode('white', 'black')(props),
      color: mode('gray.700', 'white')(props),
      pl: 4,
      py: 2,
      fontSize: 'sm',
      fontWeight: 'medium',
      rounded: 'md',
      border: 0,
      pr: 0,
      _placeholder: { color: 'gray.500' },
      ...(props.isMulti
        ? {
            _active: { bg: mode('white', 'black')(props) },
            _hover: { bg: mode('white', 'black')(props) },
          }
        : {}),
      _invalid: { borderColor: 'unset', boxShadow: 'unset' },
    },
    ToggleButton: {
      h: 8,
      position: 'absolute',
      _focus: { boxShadow: 'none' },
      bg: mode('white', 'black')(props),
      right: 1,
      top: 0,
      _hover: { bg: mode('white', 'black')(props) },
      _active: { bg: mode('white', 'black')(props) },
    },
    ClearButton: {
      h: 8,
      position: 'absolute',
      _focus: { boxShadow: 'none' },
      bg: mode('white', 'black')(props),
      top: 0,
      _hover: { bg: mode('white', 'black')(props) },
      _active: { bg: mode('white', 'black')(props) },
      right: 12,
    },
    Icon: {
      flex: 'none',
      h: 4,
      w: 4,
      mr: -1,
      color: mode('gray.400', 'white')(props),
    },
    OptionsModal: {
      position: 'fixed',
      zIndex: 10,
      overflowY: 'auto',
      w: 'inherit',
      maxH: 72,
      bg: mode('white', 'black')(props),
      my: 1,
      rounded: 'md',
      boxShadow: 'lg',
    },
    NoOptions: {
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'center',
      p: 8,
      gap: 2,
    },
    NoOptionsIcon: {
      h: 20,
    },
    Option: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'start',
      w: 'full',
      px: 4,
      py: 2.5,
      fontSize: 'sm',
      borderRadius: 0,
      borderBottom: '1px solid',
      borderColor: 'gray.300',
      _focus: { boxShadow: 'none' },
      ...(props.isSelected
        ? {
            bg: mode('gray.300', 'black')(props),
            color: isPrimaryColor(props.colorScheme)
              ? props.colorScheme
              : `${props.colorScheme}.900`,
            fontWeight: 'semibold',
          }
        : {
            _hover: {
              bg: mode('gray.200', 'whiteAlpha.300')(props),
            },
            bg: mode('white', 'gray.900')(props),
            color: 'gray.600',
          }),
    },
    Checkbox: { mr: 4 },
    OptionContainer: {
      display: 'flex',
      alignItems: 'center',
      isTruncated: true,
    },
    OptionIcon: {
      flex: 'none',
      h: 5,
      w: 5,
      mr: 3,
      color: mode('gray.400', 'white'),
    },
    OptionLabel: {
      whiteSpace: 'nowrap',
      isTruncated: true,
    },
    Tooltip: { maxW: 'full' },
  }),
  variants: {
    outline: {},
    solid: (props) => ({
      Option: {
        _active: {
          bg: isPrimaryColor(props.colorScheme)
            ? props.colorScheme
            : `${props.colorScheme}.600`,
          color: isPrimaryColor(props.colorScheme)
            ? 'white'
            : `${props.colorScheme}.200`,
        },
        _hover: {
          bg: isPrimaryColor(props.colorScheme)
            ? props.colorScheme
            : `${props.colorScheme}.600`,
          color: isPrimaryColor(props.colorScheme)
            ? 'white'
            : `${props.colorScheme}.200`,
        },
        ...(props.isSelected
          ? {
              bg: isPrimaryColor(props.colorScheme)
                ? props.colorScheme
                : `${props.colorScheme}.600`,
              color: isPrimaryColor(props.colorScheme)
                ? 'white'
                : `${props.colorScheme}.200`,
            }
          : {}),
      },
    }),
    flushed: (props) => ({
      SelectContainer: {
        borderWidth: 0,
        borderBottomWidth: '1px',
        borderColor: 'gray.300',
      },
      SearchBox: { background: 'none', _hover: { background: 'none' } },
      ToggleButton: {
        bg: mode('white', 'none')(props),
        _hover: { bg: mode('white', 'none')(props) },
        _active: { bg: mode('white', 'none')(props) },
      },
    }),
    unstyled: (props) => ({
      SelectContainer: { borderWidth: 0, boxShadow: 'none' },
      SearchBox: { background: 'none', _hover: { background: 'none' } },
      ToggleButton: {
        bg: mode('white', 'none')(props),
        _hover: { bg: mode('white', 'none')(props) },
        _active: { bg: mode('white', 'none')(props) },
      },
    }),
  },
  defaultProps: { variant: 'outline' },
};

export default Select;
